import { useEffect, useState } from "react";

/**
 * Returns true once the viewport is at least `minWidth` and the device
 * has a fine pointer (mouse), so callers can avoid mounting heavy
 * desktop-only effects (SVG filter chains, Canvas2D RAF loops,
 * cursor-parallax) on phones / tablets entirely.
 *
 * We start as `false` so the first paint is the cheapest possible
 * layout. The check fires once on mount and again on resize. We don't
 * use a single window-width check — a Surface in tablet mode or an
 * iPad with a magic keyboard should still get the touch-optimized
 * path because hit accuracy is what actually matters for the cursor
 * effects.
 */
export const useIsDesktop = (minWidth = 1024) => {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const compute = () => {
      const wide = window.innerWidth >= minWidth;
      // (hover: hover) is true on devices with a fine pointer.
      const fine = window.matchMedia("(hover: hover)").matches;
      setIsDesktop(wide && fine);
    };

    compute();
    window.addEventListener("resize", compute, { passive: true });
    return () => window.removeEventListener("resize", compute);
  }, [minWidth]);

  return isDesktop;
};

export default useIsDesktop;
